import { Space, Table } from 'antd';
import { useState } from 'react';
import { useNotify } from '../../../hooks/useNotify';
import useQueryTeamRoles from '../../../hooks/useQueryTeamRoles';
import { onApolloError } from '../../../lib/graphql/helpers';
import { shiftBackgroundColours } from '../../../theme';
import TimeDisplay from '../../Roster/TimeDisplay';
import type { ShiftPeriod } from '../../Roster/types';

type RoleAssignment = {
  readonly roleId: string;
  readonly users: readonly {
    readonly firstName: string;
    readonly lastName: string;
  }[];
};

type ReadonlyTeamProps = {
  readonly name: string;
  readonly type: string;
  readonly dayShiftPeriod?: ShiftPeriod;
  readonly roleAssignments: readonly RoleAssignment[];
};

/**
 * A table of team information.
 */
const ReadonlyTeam = (props: ReadonlyTeamProps) => {
  const { notify } = useNotify();
  const [roles, setRoles] = useState<
    readonly { readonly id: string; readonly name: string }[]
  >([]);

  useQueryTeamRoles(
    onApolloError(notify.error),
    (data) =>
      setRoles(
        data.team_role.map((role) => ({
          id: role.id,
          name: role.name,
        }))
      ),
    true
  );

  return (
    <Table
      pagination={false}
      columns={[
        {
          title: 'Field',
          dataIndex: 'field',
          key: 'field',
        },
        {
          title: 'Information',
          dataIndex: 'information',
          key: 'information',
        },
      ]}
      dataSource={[
        { key: 'teamName', field: 'Name', information: props.name },
        { key: 'teamType', field: 'Type', information: props.type },
        {
          key: 'roleAssignments',
          field: 'Role Assignments',
          information: (
            <Space direction="vertical">
              {props.roleAssignments.map((roleAssignment, i) => (
                <div key={i}>
                  <p style={{ fontWeight: 600, margin: 0 }}>
                    {roles.find((role) => role.id === roleAssignment.roleId)
                      ?.name ?? ''}
                  </p>
                  {(() => {
                    const users = roleAssignment.users
                      .map((user) => `${user.firstName} ${user.lastName}`)
                      .join(', ');
                    return users === '' ? '-' : users;
                  })()}
                </div>
              ))}
            </Space>
          ),
        },
        ...[
          props.dayShiftPeriod === undefined
            ? {}
            : {
                key: 'shiftPeriods',
                field: 'Shift Periods',
                information: (
                  <TimeDisplay
                    dayShiftPeriod={props.dayShiftPeriod}
                    shiftBackgroundColours={shiftBackgroundColours}
                    floatLeft
                  />
                ),
              },
        ],
      ]}
    />
  );
};

export default ReadonlyTeam;
